import { Button, DropdownMenu } from "@pej/kore/components";
import { ChevronDown, Pencil, Trash2 } from "lucide-react";
import type { AssetFolderSummary } from "./asset-types";

export interface FolderChipProps {
    folder: AssetFolderSummary;
    selected: boolean;
    canManage: boolean;
    onSelect: () => void;
    /** Opens the rename surface; the chip does not own it (see the note below). */
    onRename: (folder: AssetFolderSummary) => void;
    /** Opens the cascade confirmation; the chip does not own it either. */
    onDelete: (folder: AssetFolderSummary) => void;
}

// Exported so FolderScope's off-screen sizer can render a static replica at
// exactly the live chip's width. The sizer cannot render FolderChip itself —
// that would mount one DropdownMenu root per folder purely to be measured — so
// the two must share their classes or the row fits itself against the wrong
// numbers and clips the last chip mid-word.
export const CHIP_GROUP_CLASS =
    "inline-flex shrink-0 items-center gap-0.5 rounded-full border border-kore-border px-1 py-0.5";
export const CHIP_LABEL_CLASS =
    "rounded-full px-2 py-0.5 text-kore-sm outline-none focus-visible:ring-2 focus-visible:ring-kore-accent";
export const CHIP_CARET_CLASS = "size-5 shrink-0 rounded-full";

/**
 * One folder in the scope row, as a split control (spec §5.5): the label sets
 * the scope, the caret opens that folder's actions.
 *
 * This is a labelled `role="group"` of two sibling buttons rather than one
 * button containing a caret, because a button inside a button is invalid markup
 * and a real accessibility defect. It costs two tab stops per chip; that is the
 * price of not nesting. `aria-pressed` rather than radio semantics, because the
 * group also contains the caret, which is not a scope choice — a strict radio
 * group containing one would be malformed.
 *
 * The caret is revealed on hover and on keyboard focus and stays visible while
 * the folder is selected. It keeps its width when hidden (`opacity-0`, not
 * `hidden`), so hovering a chip never reflows the row it sits in — and so a
 * keyboard user can still reach it, which `display: none` would prevent.
 *
 * The chip reports rename and delete rather than rendering them. Both surfaces
 * outlive the menu that launches them and one of them is modal, so they belong
 * to the picker, which already owns the asset delete confirmation. Keeping them
 * here would put a modal dialog inside a chip that the row may re-fit away.
 */
export function FolderChip({ folder, selected, canManage, onSelect, onRename, onDelete }: FolderChipProps) {
    return (
        // biome-ignore lint/a11y/useSemanticElements: <fieldset> is for grouping form controls and carries browser-default border/margin chrome that a rounded inline chip can't use; this groups two ordinary buttons, not form inputs, so div+role="group" is the correct pattern (see docblock above).
        <div
            role="group"
            aria-label={folder.name}
            className={`${CHIP_GROUP_CLASS} ${selected ? "border-kore-accent bg-kore-accent/10" : ""} group`}
        >
            <button type="button" aria-pressed={selected} onClick={onSelect} className={CHIP_LABEL_CLASS}>
                {folder.name}
            </button>
            {canManage && (
                <DropdownMenu>
                    <DropdownMenu.Trigger
                        render={
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                aria-label={`Manage folder ${folder.name}`}
                                className={`${CHIP_CARET_CLASS} ${selected ? "" : "opacity-0"} group-hover:opacity-100 focus-visible:opacity-100 data-[popup-open]:opacity-100`}
                            >
                                <ChevronDown aria-hidden="true" />
                            </Button>
                        }
                    />
                    <DropdownMenu.Content align="start">
                        <DropdownMenu.Item onClick={() => onRename(folder)}>
                            <Pencil aria-hidden="true" />
                            Rename folder
                        </DropdownMenu.Item>
                        <DropdownMenu.Separator />
                        <DropdownMenu.Item variant="destructive" onClick={() => onDelete(folder)}>
                            <Trash2 aria-hidden="true" />
                            Delete folder
                        </DropdownMenu.Item>
                    </DropdownMenu.Content>
                </DropdownMenu>
            )}
        </div>
    );
}
